// src/services/schoolmateService.js
import { db, auth, storage } from '../config/firebase';
import { 
  collection, collectionGroup, doc, getDoc, getDocs, setDoc, addDoc,
  updateDoc, deleteDoc, query, where, orderBy, limit,
  writeBatch, serverTimestamp, increment, arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * SchoolmateService - Logs-as-Truth Architecture
 * ==============================================
 * 
 * CORE PRINCIPLE: Templates are Items, Instances are Activities, Evidence is Logs
 * 
 * ITEMS (Templates/Structure):
 * - Timetables (schedule structure)
 * - Schedule Slots (day/period/class/teacher/subject templates)
 * - Operational Plans (term initiatives)
 * 
 * ACTIVITIES (Instances - created lazily):
 * - Lessons (created when teacher opens a slot for a specific date)
 * - Assessments (test/exam tasks)
 * - Tasks (operational plan actions)
 * 
 * LOGS (Evidence/Truth - unified collection):
 * - NoteLog (free text observations)
 * - StatusLog (state transitions)
 * - AttendanceLog (present/absent/late counts + detail)
 * - CoverageLog (planned vs covered curriculum)
 * - ScoreLog (marks with strict fields)
 * 
 * Collections under pb-books/{bookId}:
 * - timetables (Items)
 *   - slots subcollection (daily lessons = periods, other types = sessions)
 * - lessons, assessments, school-activities (Activities)
 * - logs (unified log collection with type field)
 * - actors, classes, subjects (Reference data)
 */

export const LogType = {
  NOTE: 'note',
  STATUS: 'status',
  ATTENDANCE: 'attendance',
  COVERAGE: 'coverage',
  SCORE: 'score',
  INCIDENT: 'incident',
  MILESTONE: 'milestone'
};

export const ActivityType = {
  TASK: 'task',
  TODO: 'todo',
  CUSTOM: 'custom',
  MEETING: 'meeting',
  EVENT: 'event',
  PROJECT: 'project',
  NOTE: 'note',
  REFLECTION: 'reflection',
  RECORD: 'record',
  DECISION: 'decision',
  LOG: 'log'
};

export const ActivityTypeOrder = [
  ActivityType.TASK,
  ActivityType.TODO,
  ActivityType.CUSTOM,
  ActivityType.MEETING,
  ActivityType.EVENT,
  ActivityType.PROJECT,
  ActivityType.NOTE,
  ActivityType.REFLECTION,
  ActivityType.RECORD,
  ActivityType.DECISION,
  ActivityType.LOG
];

export const ActivityTypeMeta = {
  [ActivityType.TASK]: { label: 'Task', color: 'slate', colorHex: '#564f4f', description: 'Actionable items with status' },
  [ActivityType.TODO]: { label: 'To-Do', color: 'emerald', colorHex: '#059669', description: 'Lightweight checkbox items' },
  [ActivityType.CUSTOM]: { label: 'Custom', color: 'slate', colorHex: '#564f4f', description: 'User-configurable activity' },
  [ActivityType.MEETING]: { label: 'Meeting', color: 'slate', colorHex: '#564f4f', description: 'Scheduled time with participants' },
  [ActivityType.EVENT]: { label: 'Event', color: 'slate', colorHex: '#564f4f', description: 'Occasions and happenings' },
  [ActivityType.PROJECT]: { label: 'Project', color: 'slate', colorHex: '#564f4f', description: 'Container for related activities' },
  [ActivityType.NOTE]: { label: 'Note', color: 'amber', colorHex: '#F59E0B', description: 'Quick capture, ideas' },
  [ActivityType.REFLECTION]: { label: 'Reflection', color: 'violet', colorHex: '#8B5CF6', description: 'Personal thoughts, journaling' },
  [ActivityType.RECORD]: { label: 'Record', color: 'teal', colorHex: '#14B8A6', description: 'Formal entries, evidence' },
  [ActivityType.DECISION]: { label: 'Decision', color: 'orange', colorHex: '#F97316', description: 'Choices with rationale' },
  [ActivityType.LOG]: { label: 'Log', color: 'slate', colorHex: '#64748B', description: 'Timestamped operational entries' }
};

const ACTIVITY_TYPE_LEGACY_MAP = {
  assessment: ActivityType.TASK,
  excursion: ActivityType.EVENT,
  club: ActivityType.EVENT,
  sport: ActivityType.EVENT,
  admin: ActivityType.CUSTOM,
  other: ActivityType.CUSTOM
};

export const normalizeActivityType = (value, fallback = ActivityType.NOTE) => {
  if (!value) return fallback;
  if (ActivityTypeMeta[value]) return value;
  return ACTIVITY_TYPE_LEGACY_MAP[value] || fallback;
};

export const ActivityStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  ON_HOLD: 'on-hold',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  DONE: 'done',
  MISSED: 'missed'
};

export const ActivityStatusMeta = {
  [ActivityStatus.PENDING]: { label: 'Pending', color: 'slate' },
  [ActivityStatus.SCHEDULED]: { label: 'Scheduled', color: 'slate' },
  [ActivityStatus.ACTIVE]: { label: 'Active', color: 'blue' },
  [ActivityStatus.DONE]: { label: 'Done', color: 'emerald' },
  [ActivityStatus.MISSED]: { label: 'Missed', color: 'red' },
  [ActivityStatus.IN_PROGRESS]: { label: 'In Progress', color: 'blue' },
  [ActivityStatus.COMPLETED]: { label: 'Completed', color: 'emerald' },
  [ActivityStatus.CANCELLED]: { label: 'Cancelled', color: 'red' },
  [ActivityStatus.ON_HOLD]: { label: 'On Hold', color: 'amber' }
};

export const ActivityUrgency = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  CRITICAL: 'critical'
};

export const ActivityPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

export const ActivityPriorityMeta = {
  [ActivityPriority.LOW]: { label: 'Low', color: 'slate' },
  [ActivityPriority.MEDIUM]: { label: 'Medium', color: 'blue' },
  [ActivityPriority.HIGH]: { label: 'High', color: 'amber' },
  [ActivityPriority.URGENT]: { label: 'Urgent', color: 'red' }
};

export const ActivityDomain = {
  PERSONAL: 'personal',
  WORK: 'work',
  HEALTH: 'health',
  FINANCE: 'finance',
  SOCIAL: 'social',
  IDEAS: 'ideas',
  LEARNING: 'learning'
};

export const ActivityDomainMeta = {
  [ActivityDomain.PERSONAL]: { label: 'Personal', color: 'indigo' },
  [ActivityDomain.WORK]: { label: 'Work', color: 'slate' },
  [ActivityDomain.HEALTH]: { label: 'Health', color: 'green' },
  [ActivityDomain.FINANCE]: { label: 'Finance', color: 'emerald' },
  [ActivityDomain.SOCIAL]: { label: 'Social', color: 'pink' },
  [ActivityDomain.IDEAS]: { label: 'Ideas', color: 'amber' },
  [ActivityDomain.LEARNING]: { label: 'Learning', color: 'violet' }
};

export const LessonStatus = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const AttendanceStatus = {
  PRESENT: 'present',
  ABSENT: 'absent',
  LATE: 'late',
  EXCUSED: 'excused'
};

export const ItemStatus = {
  AVAILABLE: 'available',
  IN_USE: 'in-use',
  MAINTENANCE: 'maintenance',
  RETIRED: 'retired',
  MISSING: 'missing'
};

export const schoolmateService = {
  async addEntityLogCategory(bookId, entityType, entityId, category) {
    const entityCollections = {
      actor: 'actors',
      item: 'items',
      task: 'school-activities',
      activity: 'school-activities',
      lesson: 'lessons',
      assessment: 'assessments'
    };
    const collectionName = entityCollections[entityType];
    if (!collectionName) throw new Error(`Unsupported entityType: ${entityType}`);
    const entityRef = doc(db, 'pb-books', bookId, collectionName, entityId);
    await updateDoc(entityRef, {
      logCategories: arrayUnion(category),
      updatedAt: serverTimestamp()
    });
    return true;
  },

  // ============================================================
  // ACTORS (Students, Teachers, Staff) - Reference Data
  // ============================================================

  async getActors(bookId, type = null) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'actors');
      const q = type 
        ? query(ref, where('type', '==', type), orderBy('name', 'asc'))
        : query(ref, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting actors:', error);
      return [];
    }
  },

  async saveActor(bookId, actor) {
    try {
      const actorId = actor.id || doc(collection(db, 'pb-books', bookId, 'actors')).id;
      const actorRef = doc(db, 'pb-books', bookId, 'actors', actorId);
      await setDoc(actorRef, {
        ...actor, id: actorId, bookId, updatedAt: serverTimestamp(),
        ...(!actor.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      return actorId;
    } catch (error) {
      console.error('Error saving actor:', error);
      throw error;
    }
  },

  async deleteActor(bookId, actorId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'actors', actorId));
    return true;
  },

  async uploadActorPhoto(bookId, file, actorId = 'new') {
    try {
      if (!file) throw new Error('No file provided');
      if (!file.type?.startsWith('image/')) throw new Error('Please upload an image file');
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) throw new Error('Image must be under 5MB');

      const timestamp = Date.now();
      const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `pb-books/${bookId}/schoolmate/actors/${actorId}/photo/${timestamp}_${cleanName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      return {
        url,
        path,
        fileName: cleanName,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading actor photo:', error);
      throw error;
    }
  },

  // ============================================================
  // CLASSES & SUBJECTS - Reference Data
  // ============================================================

  async getClasses(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'classes');
      const q = query(ref, orderBy('grade', 'asc'), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting classes:', error);
      return [];
    }
  },

  async saveClass(bookId, classData) {
    try {
      const classId = classData.id || doc(collection(db, 'pb-books', bookId, 'classes')).id;
      const classRef = doc(db, 'pb-books', bookId, 'classes', classId);
      await setDoc(classRef, {
        ...classData, id: classId, bookId, updatedAt: serverTimestamp(),
        ...(!classData.id && { createdAt: serverTimestamp() })
      }, { merge: true });
      return classId;
    } catch (error) {
      console.error('Error saving class:', error);
      throw error;
    }
  },

  async deleteClass(bookId, classId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'classes', classId));
    return true;
  },

  async getSubjects(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'subjects');
      const q = query(ref, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting subjects:', error);
      return [];
    }
  },

  async saveSubject(bookId, subject) {
    try {
      const subjectId = subject.id || doc(collection(db, 'pb-books', bookId, 'subjects')).id;
      const subjectRef = doc(db, 'pb-books', bookId, 'subjects', subjectId);
      await setDoc(subjectRef, {
        ...subject, id: subjectId, bookId, updatedAt: serverTimestamp(),
        ...(!subject.id && { createdAt: serverTimestamp() })
      }, { merge: true });
      return subjectId;
    } catch (error) {
      console.error('Error saving subject:', error);
      throw error;
    }
  },

  async deleteSubject(bookId, subjectId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'subjects', subjectId));
    return true;
  },

  async uploadItemImage(bookId, file, itemId = 'new') {
    try {
      if (!file) throw new Error('No file provided');
      if (!file.type?.startsWith('image/')) throw new Error('Please upload an image file');
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) throw new Error('Image must be under 5MB');

      const timestamp = Date.now();
      const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `pb-books/${bookId}/schoolmate/items/${itemId}/image/${timestamp}_${cleanName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      return {
        url,
        path,
        fileName: cleanName,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading item image:', error);
      throw error;
    }
  },

  // ============================================================
  // ITEMS (Assets/Resources/Spaces)
  // ============================================================

  async getItems(bookId, filters = {}) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'items');
      let constraints = [orderBy('name', 'asc')];
      if (filters.status) constraints.unshift(where('status', '==', filters.status));
      if (filters.category) constraints.unshift(where('category', '==', filters.category));
      if (filters.location) constraints.unshift(where('location', '==', filters.location));
      const q = query(ref, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting items:', error);
      return [];
    }
  },

  async saveItem(bookId, item) {
    try {
      const itemId = item.id || doc(collection(db, 'pb-books', bookId, 'items')).id;
      const itemRef = doc(db, 'pb-books', bookId, 'items', itemId);
      const isNew = !item.id;

      await setDoc(itemRef, {
        ...item,
        id: itemId,
        bookId,
        status: item.status || ItemStatus.AVAILABLE,
        updatedAt: serverTimestamp(),
        ...(isNew && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });

      if (isNew) {
        await this.createLog(bookId, {
          type: LogType.STATUS,
          entityType: 'item',
          entityId: itemId,
          entityName: item.name,
          from: null,
          to: item.status || ItemStatus.AVAILABLE,
          note: `Item created: ${item.name}`
        });
      }

      return itemId;
    } catch (error) {
      console.error('Error saving item:', error);
      throw error;
    }
  },

  async updateItemStatus(bookId, itemId, newStatus, note = '') {
    const itemRef = doc(db, 'pb-books', bookId, 'items', itemId);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) throw new Error('Item not found');
    const item = itemSnap.data();

    await updateDoc(itemRef, { status: newStatus, updatedAt: serverTimestamp() });

    await this.createLog(bookId, {
      type: LogType.STATUS,
      entityType: 'item',
      entityId: itemId,
      entityName: item.name,
      from: item.status,
      to: newStatus,
      note: note || `Status changed: ${item.status} → ${newStatus}`
    });

    return true;
  },

  async deleteItem(bookId, itemId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'items', itemId));
    return true;
  },

  // ============================================================
  // TIMETABLES & SCHEDULE SLOTS (ITEMS - Templates)
  // ============================================================

  async getTimetables(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'timetables');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting timetables:', error);
      return [];
    }
  },

  async getActiveTimetable(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'timetables');
      const q = query(ref, where('status', '==', 'active'), limit(1));
      const snapshot = await getDocs(q);
      return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Error getting active timetable:', error);
      return null;
    }
  },

  async saveTimetable(bookId, timetable) {
    try {
      const timetableId = timetable.id || doc(collection(db, 'pb-books', bookId, 'timetables')).id;
      const timetableRef = doc(db, 'pb-books', bookId, 'timetables', timetableId);
      await setDoc(timetableRef, {
        ...timetable, id: timetableId, bookId, updatedAt: serverTimestamp(),
        ...(!timetable.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      return timetableId;
    } catch (error) {
      console.error('Error saving timetable:', error);
      throw error;
    }
  },

  async deleteTimetable(bookId, timetableId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'timetables', timetableId));
    return true;
  },

  async getScheduleSlots(bookId, timetableId = null) {
    try {
      if (timetableId) {
        let timetableType = 'lessons';
        try {
          const timetableSnap = await getDoc(doc(db, 'pb-books', bookId, 'timetables', timetableId));
          if (timetableSnap.exists()) {
            timetableType = timetableSnap.data().type || 'lessons';
          }
        } catch (err) {
          console.warn('Unable to resolve timetable type for slots query:', err);
        }
        const orderField = timetableType === 'lessons' ? 'day' : 'date';
        const ref = collection(db, 'pb-books', bookId, 'timetables', timetableId, 'slots');
        const q = query(ref, orderBy(orderField), orderBy('period'));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
        const groupRef = collectionGroup(db, 'slots');
        const groupQuery = query(
          groupRef,
          where('bookId', '==', bookId),
          where('timetableId', '==', timetableId),
          orderBy(orderField),
          orderBy('period')
        );
        const groupSnapshot = await getDocs(groupQuery);
        if (!groupSnapshot.empty) {
          return groupSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
        const legacyRef = collection(db, 'pb-books', bookId, 'schedule-slots');
        const legacyQuery = query(legacyRef, where('timetableId', '==', timetableId), orderBy(orderField), orderBy('period'));
        const legacySnapshot = await getDocs(legacyQuery);
        return legacySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      const ref = collectionGroup(db, 'slots');
      const q = query(ref, where('bookId', '==', bookId), orderBy('day'), orderBy('period'));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      const legacyRef = collection(db, 'pb-books', bookId, 'schedule-slots');
      const legacySnapshot = await getDocs(query(legacyRef, orderBy('day'), orderBy('period')));
      return legacySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting schedule slots:', error);
      return [];
    }
  },

  async saveScheduleSlot(bookId, slot) {
    try {
      const timetableId = slot.timetableId;
      if (!timetableId) throw new Error('timetableId is required for schedule slots');
      const timetableRef = doc(db, 'pb-books', bookId, 'timetables', timetableId);
      let timetableType = slot.timetableType || null;
      if (!timetableType) {
        const timetableSnap = await getDoc(timetableRef);
        timetableType = timetableSnap.exists() ? (timetableSnap.data().type || 'lessons') : null;
      }
      const slotId = slot.id || doc(collection(db, 'pb-books', bookId, 'timetables', timetableId, 'slots')).id;
      const slotRef = doc(db, 'pb-books', bookId, 'timetables', timetableId, 'slots', slotId);
      await setDoc(slotRef, {
        ...slot,
        id: slotId,
        bookId,
        timetableId,
        timetableType,
        updatedAt: serverTimestamp(),
        ...(!slot.id && { createdAt: serverTimestamp() })
      }, { merge: true });
      return slotId;
    } catch (error) {
      console.error('Error saving schedule slot:', error);
      throw error;
    }
  },

  async deleteScheduleSlot(bookId, timetableId, slotId) {
    if (timetableId) {
      await deleteDoc(doc(db, 'pb-books', bookId, 'timetables', timetableId, 'slots', slotId));
      return true;
    }
    await deleteDoc(doc(db, 'pb-books', bookId, 'schedule-slots', slotId));
    return true;
  },

  // ============================================================
  // LESSONS (ACTIVITIES - Created lazily on first interaction)
  // ============================================================

  /**
   * Get or create a lesson for a specific slot and date
   * KEY FUNCTION: Lessons only exist when a teacher accesses a slot
   */
  async getOrCreateLesson(bookId, slotId, date, timetableId = null) {
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      
      // Check if lesson already exists
      const ref = collection(db, 'pb-books', bookId, 'lessons');
      const q = query(ref, where('slotId', '==', slotId), where('date', '==', dateStr), limit(1));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }
      
      // Get slot template
      let slot = null;
      if (timetableId) {
        const slotRef = doc(db, 'pb-books', bookId, 'timetables', timetableId, 'slots', slotId);
        const slotSnap = await getDoc(slotRef);
        if (!slotSnap.exists()) throw new Error('Schedule slot not found');
        slot = slotSnap.data();
      } else {
        const slotGroupRef = collectionGroup(db, 'slots');
        const slotQuery = query(
          slotGroupRef,
          where('bookId', '==', bookId),
          where('id', '==', slotId),
          limit(1)
        );
        const slotGroupSnap = await getDocs(slotQuery);
        if (!slotGroupSnap.empty) {
          slot = slotGroupSnap.docs[0].data();
        } else {
          const legacySlotRef = doc(db, 'pb-books', bookId, 'schedule-slots', slotId);
          const legacySlotSnap = await getDoc(legacySlotRef);
          if (!legacySlotSnap.exists()) throw new Error('Schedule slot not found');
          slot = legacySlotSnap.data();
        }
      }
      
      // Create new lesson instance
      const lessonId = doc(collection(db, 'pb-books', bookId, 'lessons')).id;
      const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
      
      const lessonData = {
        id: lessonId,
        bookId,
        slotId,
        date: dateStr,
        // From slot template
        day: slot.day,
        period: slot.period,
        classId: slot.classId,
        className: slot.className,
        teacherId: slot.teacherId,
        teacherName: slot.teacherName,
        subjectId: slot.subjectId,
        subjectName: slot.subjectName,
        room: slot.room,
        startTime: slot.startTime,
        endTime: slot.endTime,
        timetableId: slot.timetableId || timetableId || null,
        timetableType: slot.timetableType || null,
        // Summary fields (updated by logs)
        status: LessonStatus.PLANNED,
        attendance: { present: 0, absent: 0, late: 0, excused: 0 },
        coverage: { planned: '', covered: '', percentage: 0 },
        engagementRating: null,
        hasMiniAssessment: false,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(lessonRef, lessonData);
      
      // Log creation
      await this.createLog(bookId, {
        type: LogType.STATUS,
        entityType: 'lesson',
        entityId: lessonId,
        slotId,
        date: dateStr,
        from: null,
        to: LessonStatus.PLANNED,
        note: 'Lesson opened'
      });
      
      return { ...lessonData, createdAt: new Date() };
    } catch (error) {
      console.error('Error getting/creating lesson:', error);
      throw error;
    }
  },

  async getLessonsForDate(bookId, date) {
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const ref = collection(db, 'pb-books', bookId, 'lessons');
      const q = query(ref, where('date', '==', dateStr), orderBy('period', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting lessons for date:', error);
      return [];
    }
  },

  async updateLesson(bookId, lessonId, updates) {
    const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
    await updateDoc(lessonRef, { ...updates, updatedAt: serverTimestamp() });
    return true;
  },

  async startLesson(bookId, lessonId) {
    const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) throw new Error('Lesson not found');
    const lesson = lessonSnap.data();
    
    await updateDoc(lessonRef, {
      status: LessonStatus.IN_PROGRESS,
      startedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    await this.createLog(bookId, {
      type: LogType.STATUS,
      entityType: 'lesson',
      entityId: lessonId,
      slotId: lesson.slotId,
      date: lesson.date,
      from: lesson.status,
      to: LessonStatus.IN_PROGRESS,
      note: 'Lesson started'
    });
    
    return true;
  },

  async completeLesson(bookId, lessonId, summary = {}) {
    const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) throw new Error('Lesson not found');
    const lesson = lessonSnap.data();
    
    await updateDoc(lessonRef, {
      status: LessonStatus.COMPLETED,
      completedAt: serverTimestamp(),
      ...summary,
      updatedAt: serverTimestamp()
    });
    
    await this.createLog(bookId, {
      type: LogType.STATUS,
      entityType: 'lesson',
      entityId: lessonId,
      slotId: lesson.slotId,
      date: lesson.date,
      from: lesson.status,
      to: LessonStatus.COMPLETED,
      note: summary.note || 'Lesson completed'
    });
    
    return true;
  },

  // ============================================================
  // LOGS (Evidence/Truth - Unified collection)
  // ============================================================

  async createLog(bookId, logData) {
    try {
      const logId = doc(collection(db, 'pb-books', bookId, 'logs')).id;
      const logRef = doc(db, 'pb-books', bookId, 'logs', logId);
      await setDoc(logRef, {
        ...logData,
        id: logId,
        bookId,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid,
        createdByName: auth.currentUser?.displayName || auth.currentUser?.email
      });
      return logId;
    } catch (error) {
      console.error('Error creating log:', error);
      throw error;
    }
  },

  async getLogs(bookId, filters = {}) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'logs');
      let constraints = [orderBy('createdAt', 'desc')];
      
      if (filters.type) constraints.unshift(where('type', '==', filters.type));
      if (filters.entityType) constraints.unshift(where('entityType', '==', filters.entityType));
      if (filters.entityId) constraints.unshift(where('entityId', '==', filters.entityId));
      if (filters.date) constraints.unshift(where('date', '==', filters.date));
      if (filters.limit) constraints.push(limit(filters.limit));
      
      const q = query(ref, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting logs:', error);
      return [];
    }
  },

  async getActivityFeed(bookId, limitCount = 100) {
    return this.getLogs(bookId, { limit: limitCount });
  },

  // ============================================================
  // ATTENDANCE LOGGING
  // ============================================================

  async recordAttendance(bookId, lessonId, attendanceData) {
    const { records, summary } = attendanceData;
    
    const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) throw new Error('Lesson not found');
    const lesson = lessonSnap.data();
    
    await this.createLog(bookId, {
      type: LogType.ATTENDANCE,
      entityType: 'lesson',
      entityId: lessonId,
      slotId: lesson.slotId,
      date: lesson.date,
      classId: lesson.classId,
      records,
      summary,
      note: `Attendance: ${summary.present}P ${summary.absent}A ${summary.late}L`
    });
    
    await updateDoc(lessonRef, {
      attendance: summary,
      attendanceCaptured: true,
      updatedAt: serverTimestamp()
    });
    
    return true;
  },

  // ============================================================
  // COVERAGE LOGGING
  // ============================================================

  async recordCoverage(bookId, lessonId, coverageData) {
    const { planned, covered, percentage, note } = coverageData;
    
    const lessonRef = doc(db, 'pb-books', bookId, 'lessons', lessonId);
    const lessonSnap = await getDoc(lessonRef);
    if (!lessonSnap.exists()) throw new Error('Lesson not found');
    const lesson = lessonSnap.data();
    
    await this.createLog(bookId, {
      type: LogType.COVERAGE,
      entityType: 'lesson',
      entityId: lessonId,
      slotId: lesson.slotId,
      date: lesson.date,
      planned,
      covered,
      percentage,
      note
    });
    
    await updateDoc(lessonRef, {
      coverage: { planned, covered, percentage },
      updatedAt: serverTimestamp()
    });
    
    return true;
  },

  // ============================================================
  // NOTE LOGGING
  // ============================================================

  async addNote(bookId, entityType, entityId, note, additionalData = {}) {
    return this.createLog(bookId, { type: LogType.NOTE, entityType, entityId, note, ...additionalData });
  },

  // ============================================================
  // ASSESSMENTS (ACTIVITIES)
  // ============================================================

  async getAssessments(bookId, filters = {}) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'assessments');
      let constraints = [orderBy('dueDate', 'desc')];
      if (filters.classId) constraints.unshift(where('classId', '==', filters.classId));
      if (filters.status) constraints.unshift(where('status', '==', filters.status));
      
      const q = query(ref, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting assessments:', error);
      return [];
    }
  },

  async saveAssessment(bookId, assessment) {
    try {
      const assessmentId = assessment.id || doc(collection(db, 'pb-books', bookId, 'assessments')).id;
      const assessmentRef = doc(db, 'pb-books', bookId, 'assessments', assessmentId);
      const isNew = !assessment.id;
      
      await setDoc(assessmentRef, {
        ...assessment,
        id: assessmentId,
        bookId,
        status: assessment.status || 'draft',
        scoresCaptured: assessment.scoresCaptured || 0,
        updatedAt: serverTimestamp(),
        ...(isNew && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      
      if (isNew) {
        await this.createLog(bookId, {
          type: LogType.STATUS,
          entityType: 'assessment',
          entityId: assessmentId,
          from: null,
          to: 'draft',
          note: `Assessment created: ${assessment.name}`
        });
      }
      
      return assessmentId;
    } catch (error) {
      console.error('Error saving assessment:', error);
      throw error;
    }
  },

  async deleteAssessment(bookId, assessmentId) {
    const logsRef = collection(db, 'pb-books', bookId, 'logs');
    const q = query(logsRef, where('entityType', '==', 'assessment'), where('entityId', '==', assessmentId));
    const snapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    snapshot.docs.forEach(d => batch.delete(d.ref));
    batch.delete(doc(db, 'pb-books', bookId, 'assessments', assessmentId));
    await batch.commit();
    return true;
  },

  // ============================================================
  // SCORE LOGS (Typed logs for marks - source of truth for marklists)
  // ============================================================

  async recordScore(bookId, assessmentId, scoreData) {
    const { actorId, actorName, score, outOf, comment, attempt = 1 } = scoreData;
    const percent = outOf > 0 ? Math.round((score / outOf) * 100) : 0;
    
    const logId = await this.createLog(bookId, {
      type: LogType.SCORE,
      entityType: 'assessment',
      entityId: assessmentId,
      actorId,
      actorName,
      score,
      outOf,
      percent,
      attempt,
      comment
    });
    
    const assessmentRef = doc(db, 'pb-books', bookId, 'assessments', assessmentId);
    await updateDoc(assessmentRef, { scoresCaptured: increment(1), updatedAt: serverTimestamp() });
    
    return logId;
  },

  async bulkRecordScores(bookId, assessmentId, scores, outOf) {
    const batch = writeBatch(db);
    
    for (const scoreData of scores) {
      const logId = doc(collection(db, 'pb-books', bookId, 'logs')).id;
      const logRef = doc(db, 'pb-books', bookId, 'logs', logId);
      const percent = outOf > 0 ? Math.round((scoreData.score / outOf) * 100) : 0;
      
      batch.set(logRef, {
        id: logId, bookId, type: LogType.SCORE, entityType: 'assessment', entityId: assessmentId,
        actorId: scoreData.actorId, actorName: scoreData.actorName,
        score: scoreData.score, outOf, percent, attempt: 1, comment: scoreData.comment || '',
        createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid
      });
    }
    
    const assessmentRef = doc(db, 'pb-books', bookId, 'assessments', assessmentId);
    batch.update(assessmentRef, { scoresCaptured: scores.length, status: 'captured', updatedAt: serverTimestamp() });
    
    await batch.commit();
    return true;
  },

  /**
   * Get marklist - VIEW over ScoreLogs
   */
  async getMarklist(bookId, assessmentId) {
    try {
      const logsRef = collection(db, 'pb-books', bookId, 'logs');
      const q = query(logsRef, where('type', '==', LogType.SCORE), where('entityId', '==', assessmentId), orderBy('actorName', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting marklist:', error);
      return [];
    }
  },

  // ============================================================
  // OPERATIONAL PLANS & ACTIVITIES (ITEMS & ACTIVITIES)
  // ============================================================

  async getOperationalPlans(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'operational-plans');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting operational plans:', error);
      return [];
    }
  },

  // ============================================================
  // PLANNER (Plans)
  // ============================================================

  async getPlannerPlans(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'planner');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting planner plans:', error);
      return [];
    }
  },

  async savePlannerPlan(bookId, plan) {
    try {
      const planId = plan.id || doc(collection(db, 'pb-books', bookId, 'planner')).id;
      const planRef = doc(db, 'pb-books', bookId, 'planner', planId);
      await setDoc(planRef, {
        ...plan, id: planId, bookId, updatedAt: serverTimestamp(),
        ...(!plan.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      return planId;
    } catch (error) {
      console.error('Error saving planner plan:', error);
      throw error;
    }
  },

  async deletePlannerPlan(bookId, planId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'planner', planId));
    return true;
  },

  async getPlannerActions(bookId, planId) {
    if (!planId) return [];
    try {
      const ref = collection(db, 'pb-books', bookId, 'planner', planId, 'actions');
      const q = query(ref, orderBy('startDate', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting planner actions:', error);
      return [];
    }
  },

  async savePlannerAction(bookId, planId, action) {
    try {
      const actionId = action.id || doc(collection(db, 'pb-books', bookId, 'planner', planId, 'actions')).id;
      const actionRef = doc(db, 'pb-books', bookId, 'planner', planId, 'actions', actionId);
      await setDoc(actionRef, {
        ...action,
        id: actionId,
        bookId,
        planId,
        status: action.status || 'planned',
        updatedAt: serverTimestamp(),
        ...( !action.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid } )
      }, { merge: true });
      return actionId;
    } catch (error) {
      console.error('Error saving planner action:', error);
      throw error;
    }
  },

  async deletePlannerAction(bookId, planId, actionId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'planner', planId, 'actions', actionId));
    return true;
  },

  async getSystemSubsystems(bookId, systemId) {
    if (!systemId) return [];
    try {
      const ref = collection(db, 'pb-books', bookId, 'systems', systemId, 'subsystems');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting system subsystems:', error);
      return [];
    }
  },

  async saveSystemSubsystem(bookId, systemId, subsystem) {
    try {
      const subsystemId = subsystem.id || doc(collection(db, 'pb-books', bookId, 'systems', systemId, 'subsystems')).id;
      const subsystemRef = doc(db, 'pb-books', bookId, 'systems', systemId, 'subsystems', subsystemId);
      await setDoc(subsystemRef, {
        ...subsystem,
        id: subsystemId,
        bookId,
        systemId,
        updatedAt: serverTimestamp(),
        ...( !subsystem.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid } )
      }, { merge: true });
      return subsystemId;
    } catch (error) {
      console.error('Error saving system subsystem:', error);
      throw error;
    }
  },

  async deleteSystemSubsystem(bookId, systemId, subsystemId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'systems', systemId, 'subsystems', subsystemId));
    return true;
  },

  async graduatePlannerAction(bookId, plan, action) {
    try {
      const activityPayload = {
        name: action.name,
        description: action.description || '',
        activityType: ActivityType.TASK,
        dueDate: action.startDate || action.endDate || '',
        startDate: action.startDate || '',
        endDate: action.endDate || '',
        assigneeId: action.assigneeId || '',
        assigneeName: action.assigneeName || '',
        indicators: action.indicators || '',
        outcomes: action.outcomes || '',
        planId: plan.id,
        planName: plan.name || '',
        references: [
          { type: 'plan', id: plan.id, name: plan.name || '' },
          { type: 'plan-action', id: action.id, name: action.name || '' }
        ],
        resources: action.resources || []
      };
      const activityId = await this.saveActivity(bookId, activityPayload);

      const actionRef = doc(db, 'pb-books', bookId, 'planner', plan.id, 'actions', action.id);
      await updateDoc(actionRef, {
        status: 'graduated',
        activityId,
        graduatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return activityId;
    } catch (error) {
      console.error('Error graduating planner action:', error);
      throw error;
    }
  },

  // ============================================================
  // SYSTEMS
  // ============================================================

  async getSystems(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'systems');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting systems:', error);
      return [];
    }
  },

  async saveSystem(bookId, system) {
    try {
      const systemId = system.id || doc(collection(db, 'pb-books', bookId, 'systems')).id;
      const systemRef = doc(db, 'pb-books', bookId, 'systems', systemId);
      await setDoc(systemRef, {
        ...system, id: systemId, bookId, updatedAt: serverTimestamp(),
        ...(!system.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      return systemId;
    } catch (error) {
      console.error('Error saving system:', error);
      throw error;
    }
  },

  async deleteSystem(bookId, systemId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'systems', systemId));
    return true;
  },

  async saveOperationalPlan(bookId, plan) {
    try {
      const planId = plan.id || doc(collection(db, 'pb-books', bookId, 'operational-plans')).id;
      const planRef = doc(db, 'pb-books', bookId, 'operational-plans', planId);
      await setDoc(planRef, {
        ...plan, id: planId, bookId, updatedAt: serverTimestamp(),
        ...(!plan.id && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      return planId;
    } catch (error) {
      console.error('Error saving operational plan:', error);
      throw error;
    }
  },

  async getActivities(bookId, filters = {}) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'school-activities');
      let constraints = [orderBy('startTime', 'asc')];
      if (filters.planId) constraints.unshift(where('planId', '==', filters.planId));
      if (filters.status) constraints.unshift(where('status', '==', filters.status));
      if (filters.activityType) constraints.unshift(where('activityType', '==', filters.activityType));
      
      const q = query(ref, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          activityType: normalizeActivityType(data.activityType, ActivityType.TASK)
        };
      });
    } catch (error) {
      console.error('Error getting activities:', error);
      return [];
    }
  },

  async migrateActivityTypes(bookId) {
    try {
      const ref = collection(db, 'pb-books', bookId, 'school-activities');
      const snapshot = await getDocs(ref);
      let batch = writeBatch(db);
      let batchCount = 0;
      let updated = 0;

      for (const snap of snapshot.docs) {
        const data = snap.data();
        const nextType = normalizeActivityType(data.activityType, ActivityType.TASK);
        if (data.activityType !== nextType) {
          batch.update(snap.ref, { activityType: nextType, updatedAt: serverTimestamp() });
          batchCount += 1;
          updated += 1;
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
      }

      if (batchCount > 0) await batch.commit();
      return updated;
    } catch (error) {
      console.error('Error migrating activity types:', error);
      return 0;
    }
  },

  async saveActivity(bookId, activity) {
    try {
      const activityId = activity.id || doc(collection(db, 'pb-books', bookId, 'school-activities')).id;
      const activityRef = doc(db, 'pb-books', bookId, 'school-activities', activityId);
      const isNew = !activity.id;
      const activityType = normalizeActivityType(activity.activityType, ActivityType.TASK);
      
      await setDoc(activityRef, {
        ...activity,
        id: activityId,
        bookId,
        status: activity.status || 'pending',
        activityType,
        updatedAt: serverTimestamp(),
        ...(isNew && { createdAt: serverTimestamp(), createdBy: auth.currentUser?.uid })
      }, { merge: true });
      
      if (isNew) {
        await this.createLog(bookId, {
          type: LogType.STATUS,
          entityType: 'activity',
          entityId: activityId,
          activityType,
          from: null,
          to: 'pending',
          note: `Activity created: ${activity.name}`
        });
      }
      return activityId;
    } catch (error) {
      console.error('Error saving activity:', error);
      throw error;
    }
  },

  async updateActivityStatus(bookId, activityId, newStatus, note = '') {
    const activityRef = doc(db, 'pb-books', bookId, 'school-activities', activityId);
    const activitySnap = await getDoc(activityRef);
    if (!activitySnap.exists()) throw new Error('Activity not found');
    const activity = activitySnap.data();
    const activityType = normalizeActivityType(activity.activityType, ActivityType.TASK);
    
    await updateDoc(activityRef, {
      status: newStatus, updatedAt: serverTimestamp(),
      ...(newStatus === 'completed' && { completedAt: serverTimestamp() })
    });
    
    await this.createLog(bookId, {
      type: LogType.STATUS,
      entityType: 'activity',
      entityId: activityId,
      activityType,
      from: activity.status,
      to: newStatus,
      note
    });
    
    return true;
  },

  async deleteActivity(bookId, activityId) {
    await deleteDoc(doc(db, 'pb-books', bookId, 'school-activities', activityId));
    return true;
  },

  // Backwards-compatible task aliases (activities)
  async getTasks(bookId, filters = {}) {
    return this.getActivities(bookId, filters);
  },

  async saveTask(bookId, task) {
    return this.saveActivity(bookId, task);
  },

  async updateTaskStatus(bookId, taskId, newStatus, note = '') {
    return this.updateActivityStatus(bookId, taskId, newStatus, note);
  },

  async deleteTask(bookId, taskId) {
    return this.deleteActivity(bookId, taskId);
  },

  // ============================================================
  // DASHBOARD HELPERS
  // ============================================================

  /**
   * Get today's schedule by expanding slot templates + merging existing lessons
   */
  async getTodaySchedule(bookId) {
    try {
      const today = new Date();
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const todayName = dayNames[today.getDay()];
      const dateStr = today.toISOString().split('T')[0];
      
      // Get slots for today
      const slotsRef = collectionGroup(db, 'slots');
      const q = query(
        slotsRef,
        where('bookId', '==', bookId),
        where('day', '==', todayName),
        orderBy('period', 'asc')
      );
      const slotsSnapshot = await getDocs(q);
      let slots = slotsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (slots.length === 0) {
        const legacyRef = collection(db, 'pb-books', bookId, 'schedule-slots');
        const legacyQuery = query(legacyRef, where('day', '==', todayName), orderBy('period', 'asc'));
        const legacySnapshot = await getDocs(legacyQuery);
        slots = legacySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      // Get existing lessons for today
      const lessonsRef = collection(db, 'pb-books', bookId, 'lessons');
      const lessonsQuery = query(lessonsRef, where('date', '==', dateStr));
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const lessonsMap = new Map();
      lessonsSnapshot.docs.forEach(doc => {
        const lesson = doc.data();
        lessonsMap.set(lesson.slotId, { id: doc.id, ...lesson });
      });
      
      // Merge: slot template + lesson instance (if exists)
      return slots.map(slot => ({
        ...slot,
        lesson: lessonsMap.get(slot.id) || null,
        hasLesson: lessonsMap.has(slot.id)
      }));
    } catch (error) {
      console.error('Error getting today schedule:', error);
      return [];
    }
  },

  async getStats(bookId) {
    try {
      const [actors, classes, subjects, assessments, activities, recentLogs] = await Promise.all([
        this.getActors(bookId),
        this.getClasses(bookId),
        this.getSubjects(bookId),
        this.getAssessments(bookId),
        this.getActivities(bookId),
        this.getLogs(bookId, { limit: 100 })
      ]);
      
      const teachers = actors.filter(a => a.type === 'teacher');
      const students = actors.filter(a => a.type === 'student');
      const pendingTasks = activities.filter(t => t.status === 'pending' || t.status === 'in-progress');
      
      const logCounts = recentLogs.reduce((acc, log) => {
        acc[log.type] = (acc[log.type] || 0) + 1;
        return acc;
      }, {});
      
      return {
        actors: { total: actors.length, teachers: teachers.length, students: students.length },
        classes: classes.length,
        subjects: subjects.length,
        assessments: { total: assessments.length },
        tasks: { total: activities.length, pending: pendingTasks.length },
        logs: logCounts,
        recentActivity: recentLogs.slice(0, 20)
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return null;
    }
  }
};

export default schoolmateService;
